import {Button, Group, Modal, NumberInput, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {type FormEvent, useEffect, useState} from 'react'

import {
    type AnalyticsScenarioParams,
    defaultScenarioRates,
    defaultScenarioThresholds,
    fullDefaultScenario,
} from 'src/lib/analytics-scenario'

type ScenarioModalProps = {
    opened: boolean
    onClose: () => void
    useRfmForEngagement: boolean
    appliedScenario: AnalyticsScenarioParams | null
    onApply: (params: AnalyticsScenarioParams) => void
    onReset: () => void
    hasOverrides: boolean
}

/** Mantine modal to edit engagement thresholds and conversion rates before applying a scenario. */
export function ScenarioModal({
    opened,
    onClose,
    useRfmForEngagement,
    appliedScenario,
    onApply,
    onReset,
    hasOverrides,
}: ScenarioModalProps) {
    const th = defaultScenarioThresholds(useRfmForEngagement)
    const rt = defaultScenarioRates()

    const [veryT, setVeryT] = useState(th.threshold_very_engaged)
    const [engT, setEngT] = useState(th.threshold_engaged)
    const [somewhatT, setSomewhatT] = useState(th.threshold_somewhat_engaged)
    const [minT, setMinT] = useState(th.threshold_minimally_engaged)

    const [rVery, setRVery] = useState(rt.rate_very_engaged * 100)
    const [rEng, setREng] = useState(rt.rate_engaged * 100)
    const [rSomewhat, setRSomewhat] = useState(rt.rate_somewhat_engaged * 100)
    const [rMin, setRMin] = useState(rt.rate_minimally_engaged * 100)
    const [rDis, setRDis] = useState(rt.rate_disengaged * 100)

    useEffect(() => {
        if (!opened) {
            return
        }
        const base = appliedScenario ?? fullDefaultScenario(useRfmForEngagement)
        setVeryT(base.threshold_very_engaged)
        setEngT(base.threshold_engaged)
        setSomewhatT(base.threshold_somewhat_engaged)
        setMinT(base.threshold_minimally_engaged)
        setRVery(base.rate_very_engaged * 100)
        setREng(base.rate_engaged * 100)
        setRSomewhat(base.rate_somewhat_engaged * 100)
        setRMin(base.rate_minimally_engaged * 100)
        setRDis(base.rate_disengaged * 100)
    }, [opened, appliedScenario, useRfmForEngagement])

    function handleApply() {
        onApply({
            threshold_very_engaged: veryT,
            threshold_engaged: engT,
            threshold_somewhat_engaged: somewhatT,
            threshold_minimally_engaged: minT,
            rate_very_engaged: rVery / 100,
            rate_engaged: rEng / 100,
            rate_somewhat_engaged: rSomewhat / 100,
            rate_minimally_engaged: rMin / 100,
            rate_disengaged: rDis / 100,
        })
        onClose()
    }

    function handleReset() {
        onReset()
        onClose()
    }

    function handleFormSubmit(event: FormEvent) {
        event.preventDefault()
        handleApply()
    }

    const scoreLabel = useRfmForEngagement ? 'RFM total' : 'MLR score'

    return (
        <Modal opened={opened} onClose={onClose} title="Scenario Planning" size="lg">
            <form onSubmit={handleFormSubmit}>
                <Stack gap="lg">
                    <Stack gap="xs">
                        <Title order={5}>Engagement thresholds</Title>
                        <Text size="sm" c="dimmed">
                            Minimum {scoreLabel} for each band (same order as live analytics).
                        </Text>
                        <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                            <NumberInput
                                label="Very Engaged (min score)"
                                value={veryT}
                                onChange={(v) => setVeryT(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Engaged (min score)"
                                value={engT}
                                onChange={(v) => setEngT(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Somewhat Engaged (min score)"
                                value={somewhatT}
                                onChange={(v) =>
                                    setSomewhatT(typeof v === 'number' ? v : Number(v))
                                }
                                min={0}
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Minimally Engaged (min score)"
                                value={minT}
                                onChange={(v) => setMinT(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                decimalScale={2}
                            />
                        </SimpleGrid>
                    </Stack>

                    <Stack gap="xs">
                        <Title order={5}>Conversion rates</Title>
                        <Text size="sm" c="dimmed">
                            Expected share of prospects at each level who convert (for yield
                            estimates).
                        </Text>
                        <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                            <NumberInput
                                label="Very Engaged"
                                value={rVery}
                                onChange={(v) => setRVery(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                max={100}
                                suffix="%"
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Engaged"
                                value={rEng}
                                onChange={(v) => setREng(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                max={100}
                                suffix="%"
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Somewhat Engaged"
                                value={rSomewhat}
                                onChange={(v) =>
                                    setRSomewhat(typeof v === 'number' ? v : Number(v))
                                }
                                min={0}
                                max={100}
                                suffix="%"
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Minimally Engaged"
                                value={rMin}
                                onChange={(v) => setRMin(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                max={100}
                                suffix="%"
                                decimalScale={2}
                            />
                            <NumberInput
                                label="Disengaged"
                                value={rDis}
                                onChange={(v) => setRDis(typeof v === 'number' ? v : Number(v))}
                                min={0}
                                max={100}
                                suffix="%"
                                decimalScale={2}
                            />
                        </SimpleGrid>
                    </Stack>

                    <Group justify="flex-end" mt="md">
                        <Button
                            type="button"
                            variant="subtle"
                            onClick={handleReset}
                            disabled={!hasOverrides}
                        >
                            Reset to defaults
                        </Button>
                        <Button type="submit">Apply</Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
